import React, { useState, useEffect } from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { useAppStateContext } from 'src/appState/AppState';
import { Popup } from 'src/domains/players/webview/ui/Popup';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import {
    Header,
    Heading,
    Container,
    Text,
    ButtonsGroup,
    ReviewLimitsButton,
    RemindLater,
    ReviewAccountsButton,
} from './NonBlockingNotifications.style';
import { action, makeObservable, observable } from 'mobx';
import { Common } from 'src/domains/common/Common';
import { AccountReviewReminderState } from 'src/domains/players/state/AccountReviewReminderState';

interface BlockingKycNotificationType {
    onConfirm: () => Promise<void>;
}
class AccountReviewNotificationState {
    @observable public showLoaderAccount: boolean = false;
    @observable public showLoaderLimits: boolean = false;
    private readonly reminderState: AccountReviewReminderState;
    private disposePromise: Promise<void> | null = null;

    public constructor(
        private readonly starRouter: StarRouter,
        private readonly onConfirm: () => Promise<void>,
        common: Common
    ) {
        makeObservable(this);
        this.reminderState = AccountReviewReminderState.get(common);
    }

    @action public setShowLoaderAccount = (value: boolean): void => {
        this.showLoaderAccount = value;
    };
    @action public setShowLoaderLimits = (value: boolean): void => {
        this.showLoaderLimits = value;
    };

    @action public handleReviewAccount = async (): Promise<void> => {
        this.setShowLoaderAccount(true);
        try {
            await this.onConfirm();
            await this.dispose();
            this.starRouter.redirectToAccountSummary();
        } catch (e) {
            console.error('Handle review of Account');
        }
        this.setShowLoaderAccount(false);
    };
    @action public handleReviewLimits = async (): Promise<void> => {
        this.setShowLoaderLimits(true);
        try {
            await this.onConfirm();
            await this.dispose();
            this.starRouter.redirectToLimits();
        } catch (e) {
            console.error('Handle review of Account');
        }
        this.setShowLoaderLimits(false);
    };

    @action public skip = async (): Promise<void> => {
        try {
            await this.onConfirm();
        } catch (e) {
            console.error('Handle skip of Account Review');
        }
    };

    public dispose = async (): Promise<void> => {
        if (this.disposePromise) {
            return this.disposePromise;
        }

        this.disposePromise = this.doDispose();
        return this.disposePromise;
    };

    private doDispose = async (): Promise<void> => {
        try {
            let result = this.reminderState.accountReviewResource.get();

            if (result.type === 'loading') {
                await new Promise((resolve) => setTimeout(resolve, 300));
                result = this.reminderState.accountReviewResource.get();
            }

            if (result.type === 'ready') {
                const data = result.value;
                if (data === null || data.lastReminderDate === null) {
                    const lastReminderDate = new Date().toISOString();
                    await this.reminderState.sendAccountReviewReminder(180, lastReminderDate);
                    await this.reminderState.accountReviewResource.refresh();
                }
            }
        } catch (error) {
            console.error('Error in dispose:', error);
        }
    };
}

export const AccountReviewNotification = observer(
    'AccountReviewNotification',
    ({ onConfirm }: BlockingKycNotificationType): JSX.Element | null => {
        const { common } = useAppStateContext();
        const starRouter = StarRouter.get(common);
        const [state] = useState(() => new AccountReviewNotificationState(starRouter, onConfirm, common));

        useEffect(() => {
            return () => {
                state.dispose();
            };
        }, [state]);

        return (
            <Popup
                overlayClose={true}
                closeButton={true}
                maxWidth='320px'
                onClose={state.skip}
            >
                <Container>
                    <Header>
                        <Heading data-test='account-review.popup.header'>
                            <I18n
                                langKey='account-review.popup.title'
                                defaultText='Time to Review Your Account'
                            />
                            <Text data-test='account-review-popup.text'>
                                <I18n
                                    langKey='account-review.popup.text.review'
                                    defaultText='As part of our commitment to Responsible Gambling we encourage you to review your account and transactions or review your current deposit limits ensuring you stay in control of your play.'
                                />
                            </Text>
                        </Heading>
                    </Header>
                    <ButtonsGroup>
                        <ReviewAccountsButton
                            size='medium'
                            isLoading={state.showLoaderAccount}
                            disabled={state.showLoaderAccount}
                            onClick={state.handleReviewAccount}
                        >
                            <I18n
                                langKey='account-review.popup.button.verify-account'
                                defaultText='Review Account'
                            />
                        </ReviewAccountsButton>
                        <ReviewLimitsButton
                            size='medium'
                            isLoading={state.showLoaderLimits}
                            disabled={state.showLoaderLimits}
                            onClick={state.handleReviewLimits}
                        >
                            <I18n
                                langKey='account-review.popup.button.verify-limits'
                                defaultText='Review Limits'
                            />
                        </ReviewLimitsButton>
                        <RemindLater onClick={state.skip}>
                            <I18n
                                langKey='account-review.popup.button.remind'
                                defaultText='Remind me later'
                            />
                        </RemindLater>
                    </ButtonsGroup>
                </Container>
            </Popup>
        );
    }
);
